import { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as SearchIcon } from 'assets/search-icon.svg';
import 'components/common/LatestTransactions/LatestTransactions.scss';
import i18nContext from 'components/i18n-context';
import Input from 'uikit/Input/Input';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TopicsFilter = ({ messagesStore, userAccount }) => {
  const i18n = useContext(i18nContext);
  const todayDate = new Date();
  const isInitialLoadRef = useRef(true);

  const statusOptions = [
    { value: 'ALL', label: i18n.getMessage('messages.filter.status.all') || 'All' },
    { value: 'OPEN', label: i18n.getMessage('messages.filter.status.open') || 'Open' },
    { value: 'IN_PROGRESS', label: i18n.getMessage('messages.filter.status.inProgress') || 'In Progress' },
    { value: 'CLOSED', label: i18n.getMessage('messages.filter.status.closed') || 'Closed' }
  ];

  useEffect(() => {
    // Skip the first render to avoid double fetching during initialization
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    // Fetch topics when filters or pagination changes (but not on initial load)
    if (userAccount?.account_number && messagesStore.isInitialized) {
      messagesStore.getTopics(userAccount.account_number);
    }
  }, [
    messagesStore.filters.statuses,
    messagesStore.filters.from,
    messagesStore.filters.to,
    messagesStore.filters.search_text,
    messagesStore.pagination.page
  ]);

  const handleSetFilter = (name, value) => {
    messagesStore.setFilter(name, value);
  };

  const handleSearchText = ({ target: { name, value } }) => {
    handleSetFilter(name, value);
  };

  const handleStatusChange = (status) => {
    handleSetFilter('statuses', status);
  };

  const handleDateChange = (name, date) => {
    handleSetFilter(name, date);
    if (name === 'from' && date && messagesStore.filters.to && date > messagesStore.filters.to) {
      handleSetFilter('to', date);
    }
  };

  return (
    <form action={'#'} className={'transaction-filter-wrapper topics-filter'}>
      <div className='tabs-wrapper'>
        {statusOptions.map((option) => (
          <button
            key={option.value || 'all'}
            type='button'
            className={`filter-tab ${messagesStore.filters.statuses === option.value ? 'active' : ''}`}
            onClick={() => handleStatusChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className={'filter-wrapper-messages'}>
        <div className={'filter-wrapper-line'}>
          <div className={`datepicker date-from ${messagesStore.filters.from ? 'selected' : ''}`}>
            <label htmlFor='from'>{i18n.getMessage('messages.filter.fromDate') || 'From'}</label>
            <DatePicker
              showMonthDropdown={true}
              showYearDropdown={true}
              isClearable={true}
              className='date-input'
              dateFormat='dd/MM/yyyy'
              name='from'
              id='from'
              placeholderText='dd/mm/yyyy'
              selected={messagesStore.filters.from}
              maxDate={todayDate}
              onChange={(date) => handleDateChange('from', date)}
            />
            {/*<CalendarIcon className='date-icon' /> */}
          </div>
          <div className={`datepicker date-to ${messagesStore.filters.to ? 'selected' : ''}`}>
            <label htmlFor='to'>{i18n.getMessage('messages.filter.toDate') || 'To'}</label>
            <DatePicker
              showMonthDropdown={true}
              showYearDropdown={true}
              isClearable={true}
              className='date-input'
              dateFormat='dd/MM/yyyy'
              name='to'
              id='to'
              placeholderText='dd/mm/yyyy'
              selected={messagesStore.filters.to}
              minDate={messagesStore.filters.from}
              maxDate={todayDate}
              onChange={(date) => handleDateChange('to', date)}
            />
            {/*<CalendarIcon className='date-icon' />*/}
          </div>
          <Input
            className={'search-message-input'}
            name={'search_text'}
            placeholder={i18n.getMessage('messages.filter.searchPlaceholder') || 'Search messages...'}
            value={messagesStore.filters.search_text}
            onChange={handleSearchText}
            Icon={SearchIcon}
          />
        </div>
      </div>
    </form>
  );
};

TopicsFilter.propTypes = {
  messagesStore: MobXPropTypes.observableObject,
  userAccount: PropTypes.object
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore,
  userAccount: stores.userStore.userData.account
}))(observer(TopicsFilter));
